export function readFileAsText(file: File, encoding?: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsText(file, encoding)
  })
}

export function readFileAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

export function readFileAsDataURL(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export function readFileAsBinaryString(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsBinaryString(file)
  })
}

export function readFileAsJSON<T = any>(file: File, encoding?: string): Promise<T> {
  return readFileAsText(file, encoding).then((text) => JSON.parse(text))
}

export function readFileAsXML(file: File, encoding?: string): Promise<Document> {
  return readFileAsText(file, encoding).then((text) => {
    const parser = new DOMParser()
    return parser.parseFromString(text, 'application/xml')
  })
}

export function readFileAsCSV(file: File, delimiter: string = ',', encoding?: string): Promise<string[][]> {
  return readFileAsText(file, encoding).then((text) => {
    const lines = text.split('\n')
    return lines.map((line) => line.split(delimiter).map((cell) => cell.trim()))
  })
}

export function arrayBufferToBlob(buffer: ArrayBuffer, type: string = 'application/octet-stream'): Blob {
  return new Blob([buffer], { type })
}

export function blobToArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as ArrayBuffer)
    reader.onerror = reject
    reader.readAsArrayBuffer(blob)
  })
}

export function blobToText(blob: Blob, encoding?: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsText(blob, encoding)
  })
}

export function blobToDataURL(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

export function textToBlob(text: string, type: string = 'text/plain'): Blob {
  return new Blob([text], { type })
}

export function dataURLToBlob(dataURL: string): Blob {
  const arr = dataURL.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1] || ''
  const bstr = atob(arr[1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new Blob([u8arr], { type: mime })
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

export function downloadText(text: string, filename: string, type: string = 'text/plain'): void {
  const blob = textToBlob(text, type)
  downloadBlob(blob, filename)
}

export function downloadJSON(data: any, filename: string): void {
  const text = JSON.stringify(data, null, 2)
  downloadText(text, filename, 'application/json')
}

export function downloadURL(url: string, filename: string): void {
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}
